/* @flow */
import React, {
  useContext, useMemo, useCallback,
} from 'react';
import {
  View, Text, StyleSheet, Platform,
} from 'react-native';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import l from '../utils/localization';
import ActionsContext from '../contexts/actions';
import DayPagingContext, { DayPagingProvider } from '../contexts/DayPagingContext';
import WeekScroller from '../components/WeekScroller';
import { OverlayContextProvider } from '../contexts/OverlayContext';
import { startMorningsAtSelector, laterTodayDelayMinutesSelector } from '../modules/settings/selectors';
import { useColors } from '../contexts/theme';
import { RefreshingProvider } from '../contexts/RefreshingContext';
import { useGetAllTodos } from '../modules/todo/actions';
import EmptyComponent from '../components/EmptyComponent';
import DraggableList from '../components/DraggableList';
import Overlay from '../components/Overlay';


const styles = StyleSheet.create({
  WeekScrollerWrapper: {
    position: Platform.OS === 'web' ? 'fixed' : undefined,
    left: Platform.OS === 'web' ? 0 : undefined,
    right: Platform.OS === 'web' ? 0 : undefined,
    backgroundColor: 'white',
    shadowColor: 'black',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 1,
    zIndex: 999999999,
  },
});

const ListEmptyComponent = () => {
  const colors = useColors(),
        { selectedDate } = useContext(DayPagingContext),
        uniqueness = selectedDate.date();

  return (
    <EmptyComponent uniqueness={uniqueness}>
      <Text
        style={{ color: colors.text, textAlign: 'center' }}
      >
        { l.nothingPlanned }
      </Text>
    </EmptyComponent>
  );
};

const List = (props: Object) => {
  const actions = useContext(ActionsContext),
        rightActions = useMemo(() => [actions.todo, actions.done, actions.delete], [actions]),
        { data } = props,
        leftActions = useMemo(() => [actions.postpone], [actions]);

  const showAnimation = data.length === 0;

  return (
    <>
      <DraggableList
        rightActions={rightActions}
        leftActions={leftActions}
        ListEmptyComponent={ListEmptyComponent}
        {...props}
      />
      <Overlay showAnimation={showAnimation} />
    </>
  );
};


const TodoListPostponed = (props: Object) => {
  const onRefresh = useGetAllTodos(),
        startMorningsAt = useSelector(startMorningsAtSelector),
        onRefreshFn = useCallback(() => onRefresh(true), [onRefresh]),
        laterTodayDelayMinutes = useSelector(laterTodayDelayMinutesSelector);

  return useMemo(() => (
    <RefreshingProvider onRefresh={onRefreshFn}>
      <DayPagingProvider>
        <DayPagingContext.Consumer>
          {
            ({ selectedDate, dataPerDay }) => {
              const data = dataPerDay[selectedDate.valueOf()] || [],
                    manualSortOrder = data && data.length > 0 ? data[0].manualSortOrder + 1 : 0;

              let postponedUntil = selectedDate.startOf('day').add(startMorningsAt, 'minutes').toDate();
              if (postponedUntil < new Date()) {
                postponedUntil = dayjs().add(laterTodayDelayMinutes, 'minutes').startOf('hour').toDate();
              }

              return (
                <OverlayContextProvider newTaskTemplate={{ status: 'postponed', postponedUntil, manualSortOrder }}>
                  <View
                    style={styles.WeekScrollerWrapper}
                    elevation={7}
                  >
                    <WeekScroller />
                  </View>
                  <List {...props} data={data} />

                </OverlayContextProvider>
              );
            }
          }

        </DayPagingContext.Consumer>
      </DayPagingProvider>
    </RefreshingProvider>
  ), [laterTodayDelayMinutes, onRefreshFn, props, startMorningsAt]);
};

TodoListPostponed.whyDidYouRender = true;

export default TodoListPostponed;
